import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { apiGetSellableCards, apiSellCard } from '../../../api/cardApi';
import { apiGetMyCardAccount } from '../../../api/userCardApi';
import type { SellableCard, CardRarity } from '../../../types/models/card';

const storageUrl = process.env.REACT_APP_STORAGE_URL ?? '';
const toImgSrc = (path: string | null | undefined) =>
  path ? (path.startsWith('http') ? path : storageUrl + path) : null;

const RARITY_COLOR: Record<CardRarity, string> = {
  common: 'rgba(255,255,255,0.55)',
  rare: '#4a9eff',
  epic: '#c27ff5',
  legendary: 'gold',
};

const RARITY_LABEL: Record<CardRarity, string> = {
  common: 'Comune',
  rare: 'Rara',
  epic: 'Epica',
  legendary: 'Leggendaria',
};

// ─── Styled ───────────────────────────────────────────────────────────────────

const Container = styled.div`
  padding: 28px 16px 80px;
  min-height: calc(100vh - 70px);
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 12px;
`;

const TitleBlock = styled.div``;

const PageTitle = styled.h1`
  font-size: 22px;
  font-weight: 900;
  color: #10b981;
  margin: 0 0 4px;
`;

const PageSub = styled.p`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.4);
  margin: 0;
`;

const CreditsBox = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  background: rgba(16, 185, 129, 0.08);
  border: 1px solid rgba(16, 185, 129, 0.3);
  border-radius: 8px;
  padding: 8px 14px;
`;

const CreditsLabel = styled.span`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.4);
`;

const CreditsValue = styled.span`
  font-size: 18px;
  font-weight: 900;
  color: #10b981;
`;

const EmptyText = styled.p`
  color: rgba(255, 255, 255, 0.35);
  text-align: center;
  padding: 60px 0;
  font-size: 14px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
`;

const CardWrapper = styled.div`
  background: #111;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.07);
  display: flex;
  flex-direction: column;
`;

const RarityBar = styled.div<{ $color: string }>`
  height: 3px;
  background: ${({ $color }) => $color};
`;

const CardImgArea = styled.div`
  aspect-ratio: 2 / 3;
  background: #0d0d0d;
  overflow: hidden;
`;

const CardImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
`;

const CardImgPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  color: rgba(255, 255, 255, 0.1);
`;

const CardInfo = styled.div`
  padding: 8px 10px 10px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
`;

const RarityText = styled.span<{ $color: string }>`
  font-size: 10px;
  font-weight: 700;
  color: ${({ $color }) => $color};
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const WrestlerNames = styled.div`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.55);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-height: 14px;
`;

const SmallInfo = styled.div`
  font-size: 10px;
  color: rgba(255, 255, 255, 0.28);
`;

const QtyRow = styled.div`
  display: flex;
  align-items: center;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 5px;
  overflow: hidden;
`;

const QtyBtn = styled.button`
  width: 28px;
  height: 28px;
  background: rgba(255, 255, 255, 0.04);
  border: none;
  color: rgba(255, 255, 255, 0.6);
  font-size: 15px;
  cursor: pointer;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  &:disabled { opacity: 0.25; cursor: default; }
`;

const QtyValue = styled.span`
  flex: 1;
  text-align: center;
  font-size: 13px;
  font-weight: 700;
  color: #fff;
`;

const ReceiveRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ReceiveLabel = styled.span`
  font-size: 10px;
  color: rgba(255, 255, 255, 0.3);
`;

const ReceiveValue = styled.span`
  font-size: 12px;
  font-weight: 700;
  color: #10b981;
`;

const SellBtn = styled.button`
  width: 100%;
  padding: 8px;
  background: rgba(16, 185, 129, 0.1);
  border: 1px solid rgba(16, 185, 129, 0.4);
  border-radius: 5px;
  color: #10b981;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  margin-top: auto;
`;

// ─── Confirm modal ────────────────────────────────────────────────────────────

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.8);
  z-index: 1000;
  display: flex;
  align-items: flex-end;
  padding: 0;
`;

const ModalSheet = styled.div`
  background: #1c1c1c;
  border-radius: 16px 16px 0 0;
  padding: 24px 20px 36px;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ModalHandle = styled.div`
  width: 36px;
  height: 4px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 2px;
  align-self: center;
  margin-bottom: 4px;
`;

const ModalTitle = styled.h3`
  font-size: 16px;
  font-weight: 800;
  color: #fff;
  margin: 0;
`;

const ModalBody = styled.p`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.5);
  margin: 0;
  line-height: 1.6;
`;

const ModalCredits = styled.div`
  font-size: 26px;
  font-weight: 900;
  color: #10b981;
  text-align: center;
  padding: 12px;
  background: rgba(16, 185, 129, 0.08);
  border-radius: 8px;
`;

const ModalActions = styled.div`
  display: flex;
  gap: 10px;
`;

const CancelBtn = styled.button`
  flex: 1;
  padding: 12px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.6);
  font-size: 14px;
  cursor: pointer;
`;

const ConfirmBtn = styled.button`
  flex: 2;
  padding: 12px;
  background: rgba(16, 185, 129, 0.12);
  border: 1px solid #10b981;
  border-radius: 8px;
  color: #10b981;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;

  &:disabled { opacity: 0.5; cursor: default; }
`;

const ErrorMsg = styled.p`
  font-size: 12px;
  color: #f87171;
  margin: 0;
  text-align: center;
`;

// ─── Component ────────────────────────────────────────────────────────────────

const CediCarteMobile: React.FC = () => {
  const [cards, setCards] = useState<SellableCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [quantities, setQuantities] = useState<Record<number, number>>({});
  const [credits, setCredits] = useState<number | null>(null);
  const [confirmCard, setConfirmCard] = useState<SellableCard | null>(null);
  const [selling, setSelling] = useState(false);
  const [sellError, setSellError] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([apiGetSellableCards(), apiGetMyCardAccount()])
      .then(([sellable, account]) => {
        setCards(sellable);
        setCredits(account.credits);
        const initQty: Record<number, number> = {};
        sellable.forEach((c) => { initQty[c.id] = 1; });
        setQuantities(initQty);
      })
      .catch(() => setCards([]))
      .finally(() => setLoading(false));
  }, []);

  const getQty = (card: SellableCard) => quantities[card.id] ?? 1;

  const changeQty = (card: SellableCard, delta: number) => {
    setQuantities((prev) => ({
      ...prev,
      [card.id]: Math.max(1, Math.min(card.sellable_quantity, (prev[card.id] ?? 1) + delta)),
    }));
  };

  const getCreditsPreview = (card: SellableCard) =>
    (quantities[card.id] ?? 1) * card.sell_price;

  const getApiQty = (card: SellableCard) => {
    const qty = quantities[card.id] ?? 1;
    return card.is_bundle ? qty * card.bundle_size : qty;
  };

  const openConfirm = (card: SellableCard) => {
    setSellError(null);
    setConfirmCard(card);
  };

  const handleConfirm = async () => {
    if (!confirmCard) return;
    setSelling(true);
    setSellError(null);
    try {
      const res = await apiSellCard(confirmCard.id, getApiQty(confirmCard));
      setCredits(res.credits_remaining);
      const soldQty = quantities[confirmCard.id] ?? 1;
      setCards((prev) =>
        prev
          .map((c) =>
            c.id === confirmCard.id
              ? { ...c, sellable_quantity: c.sellable_quantity - soldQty }
              : c
          )
          .filter((c) => c.sellable_quantity > 0)
      );
      setQuantities((prev) => ({ ...prev, [confirmCard.id]: 1 }));
      setConfirmCard(null);
    } catch (e: any) {
      setSellError(e?.message ?? 'Errore durante la cessione. Riprova.');
    } finally {
      setSelling(false);
    }
  };

  const describeSelection = (card: SellableCard) => {
    const qty = quantities[card.id] ?? 1;
    if (card.is_bundle) return `${qty} bundle (${qty * card.bundle_size} carte)`;
    return `${qty} ${qty === 1 ? 'carta' : 'carte'}`;
  };

  return (
    <Container>
      <PageHeader>
        <TitleBlock>
          <PageTitle>Cedi Carte</PageTitle>
          <PageSub>Cedi le copie in eccesso per crediti</PageSub>
        </TitleBlock>
        {credits !== null && (
          <CreditsBox>
            <CreditsLabel>Crediti:</CreditsLabel>
            <CreditsValue>{credits}</CreditsValue>
          </CreditsBox>
        )}
      </PageHeader>

      {loading && <EmptyText>Caricamento...</EmptyText>}
      {!loading && cards.length === 0 && (
        <EmptyText>Nessuna carta disponibile per la cessione.</EmptyText>
      )}

      {!loading && cards.length > 0 && (
        <Grid>
          {cards.map((card) => {
            const src = toImgSrc(card.fronte_path);
            const rColor = RARITY_COLOR[card.rarity];
            const qty = getQty(card);
            const wrestlers = card.wrestlers.slice(0, 1).map((w) => w.name).join(', ');
            return (
              <CardWrapper key={card.id}>
                <RarityBar $color={rColor} />
                <CardImgArea>
                  {src
                    ? <CardImg src={src} alt={`#${card.id}`} />
                    : <CardImgPlaceholder>#{card.id}</CardImgPlaceholder>
                  }
                </CardImgArea>
                <CardInfo>
                  <RarityText $color={rColor}>{RARITY_LABEL[card.rarity]}</RarityText>
                  <WrestlerNames>{wrestlers || `#${card.id}`}</WrestlerNames>
                  <SmallInfo>
                    {card.sellable_quantity} cedibili
                    {card.is_bundle && ` · ×${card.bundle_size}`}
                  </SmallInfo>

                  <QtyRow>
                    <QtyBtn onClick={() => changeQty(card, -1)} disabled={qty <= 1}>−</QtyBtn>
                    <QtyValue>{qty}</QtyValue>
                    <QtyBtn onClick={() => changeQty(card, +1)} disabled={qty >= card.sellable_quantity}>+</QtyBtn>
                  </QtyRow>

                  <ReceiveRow>
                    <ReceiveLabel>Ricevi:</ReceiveLabel>
                    <ReceiveValue>+{getCreditsPreview(card)}</ReceiveValue>
                  </ReceiveRow>

                  <SellBtn onClick={() => openConfirm(card)}>Cedi</SellBtn>
                </CardInfo>
              </CardWrapper>
            );
          })}
        </Grid>
      )}

      {confirmCard && (
        <ModalOverlay onClick={(e) => e.target === e.currentTarget && !selling && setConfirmCard(null)}>
          <ModalSheet>
            <ModalHandle />
            <ModalTitle>Conferma cessione</ModalTitle>
            <ModalBody>
              Stai cedendo <strong style={{ color: '#fff' }}>{describeSelection(confirmCard)}</strong>
              {confirmCard.wrestlers.length > 0 && (
                <> di <strong style={{ color: '#fff' }}>{confirmCard.wrestlers[0].name}</strong></>
              )}.
            </ModalBody>
            <ModalCredits>+{getCreditsPreview(confirmCard)} crediti</ModalCredits>
            {sellError && <ErrorMsg>{sellError}</ErrorMsg>}
            <ModalActions>
              <CancelBtn onClick={() => setConfirmCard(null)} disabled={selling}>Annulla</CancelBtn>
              <ConfirmBtn onClick={handleConfirm} disabled={selling}>
                {selling ? 'In corso...' : 'Conferma'}
              </ConfirmBtn>
            </ModalActions>
          </ModalSheet>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default CediCarteMobile;
